import React, { useContext, useEffect, useRef, useState } from 'react';
import * as R from 'ramda';

import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { Icon, IconButton, Popover } from '@atom/mui';
import { KeyboardArrowRightIcon, MoreHorizIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { InventoryCategoryTree } from '@atom/types/category';

import InventoryPortalContext from './InventoryPortalContext';

import classes from './inventoryPortal.module.css';

interface Breadcrumb {
  id: string;
  name: string;
  path: any[];
}

const hydratePaths = (
  categoryTree: InventoryCategoryTree,
  activePath: any[],
): Breadcrumb[] => {
  return activePath.reduce((acc, item, index) => {
    const path = R.slice(0, R.inc(index), activePath);

    return R.equals('categories', item)
      ? acc
      : [...acc, { ...R.pathOr({}, path, categoryTree), path }];
  }, []);
};

const INVENTORY_BREADCRUMB: Breadcrumb = {
  id: 'inventory',
  name: 'Inventory',
  path: [],
};

const InventoryBreadcrumbs = () => {
  const container = useRef<HTMLDivElement>(null);
  const [, { updateSearchParams }] = useNavigation({
    id: SearchParamType.STRING,
  });

  const [anchor, setAnchor] = useState<any>(null);
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [overflowing, setOverflowing] = useState<boolean>(false);

  const { categoryTree, activePath, setActiveCategory } = useContext(
    InventoryPortalContext,
  );

  const breadcrumbs = hydratePaths(categoryTree, activePath);
  const activeBreadcrumb = R.last(breadcrumbs) || INVENTORY_BREADCRUMB;
  const lastIndex = R.dec(breadcrumbs.length);
  const overflowBreadcrumbs = R.slice(
    0,
    R.dec(breadcrumbs.length),
    breadcrumbs,
  );

  const handleToggle = event => {
    setAnchor(event.currentTarget);
    setPopoverOpen(true);
  };

  const handleClick = (path: any[]) => {
    const activeCategory = R.pathOr({}, path, categoryTree);
    // @ts-ignore
    setActiveCategory(activeCategory, path);
    updateSearchParams({ id: '' });

    setPopoverOpen(false);
  };

  useEffect(() => {
    setOverflowing(
      container.current.offsetHeight < container.current.scrollHeight,
    );
  }, [activePath]);

  const arrow = (
    <Icon color={colors.neutral.white} icon={KeyboardArrowRightIcon} />
  );

  const inventoryBreadcrumb = (
    <>
      <div className={classes.breadcrumb} onClick={() => handleClick([])}>
        Inventory
      </div>
      {Boolean(breadcrumbs.length) && arrow}
    </>
  );

  const overflowContent = (
    <div
      className={classes.breadcrumbContainer}
      style={{ visibility: overflowing ? 'visible' : 'hidden' }}
    >
      {inventoryBreadcrumb}
      <IconButton onClick={handleToggle}>
        <Icon color={colors.neutral.white} icon={MoreHorizIcon} />
      </IconButton>
      <Popover
        open={popoverOpen}
        anchorEl={anchor}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        onClose={() => setPopoverOpen(false)}
      >
        {overflowBreadcrumbs.map(breadcrumb => (
          <div
            key={breadcrumb.name + breadcrumb.id}
            onClick={() => handleClick(breadcrumb.path)}
            className={classes.breadcrumbMenuItem}
          >
            {breadcrumb.name}
          </div>
        ))}
      </Popover>
      {arrow}
      <span
        className={classes.breadcrumb}
        onClick={() => handleClick(activeBreadcrumb?.path)}
      >
        {activeBreadcrumb?.name}
      </span>
    </div>
  );

  const regularContent = (
    <div
      className={classes.breadcrumbContainer}
      ref={container}
      style={{ visibility: overflowing ? 'hidden' : 'visible' }}
    >
      {inventoryBreadcrumb}
      {breadcrumbs.map((breadcrumb, index) => (
        <React.Fragment key={breadcrumb.id}>
          <div
            className={classes.breadcrumb}
            onClick={() => handleClick(breadcrumb.path)}
          >
            {breadcrumb.name}
          </div>
          {index !== lastIndex && arrow}
        </React.Fragment>
      ))}
    </div>
  );

  return overflowing ? (
    <>
      {overflowContent}
      {regularContent}
    </>
  ) : (
    <>
      {regularContent}
      {overflowContent}
    </>
  );
};

export default InventoryBreadcrumbs;
